import {
  Avatar,
  Tag,
  Space,
  Button,
  Popconfirm,
  message,
  Menu,
  Dropdown,
} from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined, HistoryOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import moment from "moment";
import _get from "lodash/get";
import { useDispatch } from "react-redux";
import { setPopup } from "providers/GeneralProvider/slice";
import { deleteCandidateRequest } from "providers/CandidateProvider/slice";
import Helper from "utils/helpers";
import VoteHistoryModal from "components/Candidate/VoteHistoryModal";

const MenuOverlay = ({ record, refetchList }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showVoteHistory, setShowVoteHistory] = useState(false);

  const handleDeleteCandidate = () => {
    dispatch(
      setPopup({
        isOpen: true,
        data: {
          okText: "Xác nhận",
          cancelText: "Hủy",
          title: "Xác nhận xóa thí sinh",
          content: `Bạn có muốn xóa thí sinh ${record.fullName}?`,
          onOk: () => {
            dispatch(deleteCandidateRequest(record._id))
              .then(() => {
                Helper.toastr(
                  "Xóa thí sinh thành công",
                  "Thí sinh đã được xóa thành công",
                  "success"
                );
                refetchList();
              })
              .catch((error) => {
                Helper.toastr(
                  "Xóa thí sinh thất bại",
                  "Có lỗi xảy ra khi xóa thí sinh. Vui lòng thử lại.",
                  "error"
                );
              });
          },
        },
      })
    );
  };

  return (
    <>
      <Menu>
        <Menu.Item
          icon={<EyeOutlined />}
          onClick={() => navigate(`/voting/candidate/${record._id}/view`)}
        >
          Xem chi tiết
        </Menu.Item>
        <Menu.Item
          icon={<EditOutlined />}
          onClick={() => navigate(`/voting/candidate/${record._id}/edit`)}
        >
          Chỉnh sửa
        </Menu.Item>
        <Menu.Item
          icon={<HistoryOutlined />}
          onClick={() => setShowVoteHistory(true)}
        >
          Lịch sử bình chọn
        </Menu.Item>
        <Menu.Item
          icon={<DeleteOutlined />}
          danger
          onClick={handleDeleteCandidate}
        >
          Xóa thí sinh
        </Menu.Item>
      </Menu>

      <VoteHistoryModal
        candidateId={record._id}
        candidateName={record.fullName}
        visible={showVoteHistory}
        onClose={() => setShowVoteHistory(false)}
      />
    </>
  );
};

const CandidateColumn = ({ onRefresh, pagination }) => {
  const navigate = useNavigate();

  const handleEdit = (record) => {
    navigate(`/voting/candidate/${record._id}/edit`);
  };

  const handleView = (record) => {
    navigate(`/voting/candidate/${record._id}/view`);
  };

  const handleDelete = async (record) => {
    try {
      // TODO: Implement delete candidate API
      message.success("Xóa thí sinh thành công");
      onRefresh();
    } catch (error) {
      message.error("Có lỗi xảy ra khi xóa thí sinh");
    }
  };

  const getMenuItems = (record) => [
    {
      key: "edit",
      label: "Chỉnh sửa",
      icon: <EditOutlined />,
      onClick: () => handleEdit(record),
    },
    {
      key: "view",
      label: "Xem chi tiết",
      icon: <EyeOutlined />,
      onClick: () => handleView(record),
    },
    {
      key: "delete",
      label: "Xóa",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleDelete(record),
    },
  ];

  return [
    {
      title: "Xếp hạng",
      key: "ranking",
      width: 80,
      align: "center",
      render: (_, record, index) => {
        // Calculate ranking based on current page and position
        const currentPage = pagination?.page || 1;
        const pageSize = pagination?.limit || 10;
        const ranking = (currentPage - 1) * pageSize + index + 1;
        
        // Add special styling for top 3
        let rankingStyle = {
          fontWeight: "bold",
          fontSize: "14px",
          padding: "4px 8px",
          borderRadius: "4px",
          display: "inline-block",
          minWidth: "30px",
          textAlign: "center"
        };
        
        if (ranking === 1) {
          rankingStyle = {
            ...rankingStyle,
            backgroundColor: "#FFD700",
            color: "#000",
            border: "2px solid #FFA500"
          };
        } else if (ranking === 2) {
          rankingStyle = {
            ...rankingStyle,
            backgroundColor: "#C0C0C0",
            color: "#000",
            border: "2px solid #A0A0A0"
          };
        } else if (ranking === 3) {
          rankingStyle = {
            ...rankingStyle,
            backgroundColor: "#CD7F32",
            color: "#fff",
            border: "2px solid #B8860B"
          };
        } else {
          rankingStyle = {
            ...rankingStyle,
            backgroundColor: "#f5f5f5",
            color: "#666",
            border: "1px solid #d9d9d9"
          };
        }
        
        return (
          <div style={rankingStyle}>
            {ranking === 1 && "🥇"}
            {ranking === 2 && "🥈"}
            {ranking === 3 && "🥉"}
            {ranking > 3 && ranking}
          </div>
        );
      },
    },
    {
      title: "Thí sinh",
      dataIndex: "fullName",
      key: "fullName",
      width: 200,
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            size={40}
            src={_get(record, "images.0.url")}
            style={{ flexShrink: 0 }}
          >
            {text?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{text}</div>
            <div style={{ fontSize: 12, color: "#666" }}>
              Số báo danh: {record.contestNumber}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Cuộc thi",
      dataIndex: "contestId",
      key: "contestId",
      width: 150,
      render: (contestId, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {contestId?.name || _get(record, "contest.name", "N/A")}
          </div>
        </div>
      ),
    },
    {
      title: "Quê quán",
      dataIndex: "hometown",
      key: "hometown",
      width: 120,
      render: (text) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Nơi ở hiện tại",
      dataIndex: "currentResidence",
      key: "currentResidence",
      width: 150,
      render: (text) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Lượt bình chọn",
      dataIndex: "voteCount",
      key: "voteCount",
      width: 120,
      render: (text) => (
        <Tag color="blue" style={{ fontWeight: 500 }}>
          {text || 0}
        </Tag>
      ),
    },
    {
      title: "Tổng điểm",
      dataIndex: "totalScore",
      key: "totalScore",
      width: 120,
      render: (text) => (
        <Tag color="purple" style={{ fontWeight: 500 }}>
          {text || 0}
        </Tag>
      ),
    },
    {
      title: "Tương tác",
      dataIndex: "totalEngagement",
      key: "totalEngagement",
      width: 120,
      render: (text, record) => {
        // Calculate total engagement from various sources
        const engagement =
          text ||
          (record.likeCount || 0) +
            (record.commentCount || 0) +
            (record.shareCount || 0) +
            (record.reactions
              ? Object.values(record.reactions).reduce(
                  (sum, val) => sum + (val || 0),
                  0
                )
              : 0);

        return (
          <Tag color="green" style={{ fontWeight: 500 }}>
            {engagement}
          </Tag>
        );
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text) => {
        const statusConfig = {
          active: { color: "green", text: "Hoạt động" },
          inactive: { color: "red", text: "Không hoạt động" },
          draft: { color: "orange", text: "Bản nháp" },
        };
        const config = statusConfig[text] || { color: "default", text };
        return (
          <Tag color={config.color} style={{ fontWeight: 500 }}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (text) => (
        <span>{text ? moment(text).format("DD/MM/YYYY") : "N/A"}</span>
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          overlay={<MenuOverlay record={record} refetchList={onRefresh} />}
          trigger={["click"]}
        >
          <a onClick={(e) => e.preventDefault()}>Menu</a>
        </Dropdown>
      ),
    },
  ];
};

export default CandidateColumn;
